import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Card } from "react-bootstrap";
import { snackbar } from "@mobiscroll/react5";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const TncTextEditorPopup = ({ show, closePopup, onSave, fileType }) => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const quillRef = useRef(null);

  // Fix common OCR / encoding / mojibake problems
  const cleanCorruptedText = (text = "") => {
    if (!text) return "";

    return text
      .normalize("NFKC")
      // Target the specific mojibake sequences
      .replace(/\u00e2\u20ac\u201c/g, "—") // em dash
      .replace(/\u00e2\u20ac\u2013/g, "–") // en dash
      .replace(/\u00e2\u20ac\u02dc/g, "'")  // left single quote
      .replace(/\u00e2\u20ac\u2122/g, "'")  // right single quote
      .replace(/\u00e2\u20ac\u0153/g, '"')  // left double quote
      .replace(/\u00e2\u20ac\u009d/g, '"')  // right double quote
      .replace(/\u00c2\u00a0/g, " ")       // non-breaking space
      // Standard cleanup
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .trim();
  };

  // Clean only text nodes while preserving formatting tags
  const cleanHtmlTextNodes = (html = "") => {
    if (!html) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const walk = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          child.textContent = cleanCorruptedText(child.textContent || "");
        } else {
          walk(child);
        }
      });
    };

    walk(doc.body);
    return doc.body.innerHTML;
  };

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ direction: "rtl" }],
        [{ align: [] }],
        ["clean"],
      ],
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "script",
    "indent",
    "direction",
    "align",
  ];

  // Register paste cleaner once Quill is ready
  useEffect(() => {
    if (!show || !quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const clipboard = editor.getModule("clipboard");

    if (!clipboard || clipboard.__tncCleanerInstalled) return;

    // Clean every pasted text node before Quill inserts it
    clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
      const cleaned = cleanCorruptedText(node.data || "");

      delta.ops = (delta.ops || []).map((op) => {
        if (typeof op.insert === "string") {
          return {
            ...op,
            insert: cleanCorruptedText(op.insert),
          };
        }
        return op;
      });

      // fallback if delta was empty
      if (!delta.ops || delta.ops.length === 0) {
        delta.ops = [{ insert: cleaned }];
      }

      return delta;
    });

    clipboard.__tncCleanerInstalled = true;
  }, [show]);

  const handleEditorChange = (value) => {
    setContent(value);
  };

  const handleSave = async () => {
    const cleanedContent = cleanHtmlTextNodes(content);

    // remove tags to validate meaningful text
    const plainText = cleanedContent.replace(/<[^>]*>/g, "").trim();

    if (!plainText) {
      snackbar({
        message: "Please enter some content",
        color: "danger",
      });
      return;
    }

    setLoading(true);

    try {
      const blob = new Blob(["\ufeff", cleanedContent], { type: "text/html;charset=utf-8" });
      const file = new File([blob], `${fileType}.html`, {
        type: "text/html;charset=utf-8",
      });

      const success = await onSave(file, fileType);

      if (success) {
        setContent("");
        closePopup();
        snackbar({
          message: "Terms and Conditions saved successfully",
          color: "success",
        });
      }
    } catch (error) {
      console.error("Error saving content:", error);
      snackbar({
        message: "Error saving content",
        color: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setContent("");
    closePopup();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      dialogClassName="docg-modal"
      size="lg"
      centered
    >
      <Modal.Header
        closeButton
        style={{ backgroundColor: "#DEE2E6", color: "#6C5B7B" }}
      >
        <Modal.Title>Create Terms and Conditions</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Card>
          <Card.Body>
            {loading && (
              <div className="d-flex justify-content-center align-items-center mb-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <span className="ml-2">Uploading Terms and Conditions...</span>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label">
                <strong>Enter Terms and Conditions Content:</strong>
              </label>

              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={content}
                onChange={handleEditorChange}
                modules={modules}
                formats={formats}
                placeholder="Start typing here..."
                style={{ height: "400px", marginBottom: "50px" }}
              />
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="docg-patient-cancel-btn mr-2"
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSave}
                className="docg-save-btn"
                disabled={loading}
              >
                Save & Upload
              </button>
            </div>
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

export default TncTextEditorPopup;